import {
  FilterBuilder,
  useFilterContext,
} from "@/components/landing/FeeFilter";
import { usePage } from "@/components/page/PageContext";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { getAuthToken } from "@/lib/auth";
import { API_ENDPOINT } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { MultiSeriesChart } from "@/pages/data/DataChart";
import {
  getAvailableDates,
  getRangeReport,
  MultiDayAggregateFees,
} from "@/queries/landing";
import { useDateContext } from "@/stores/DateContext";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import * as React from "react";
import { useEffect } from "react";

function SingleDatePicker({
  date,
  onSelect,
  label,
}: {
  date: Date | undefined;
  onSelect: (date: Date | undefined) => void;
  label: string;
}) {
  return (
    <div>
      <div className="uppercase mb-2 text-slate-400">{label}:</div>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant={"outline"}
            className={cn(
              "w-[160px] sm:w-[200px] justify-start text-left font-normal",
              !date && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date ? format(date, "LLL dd, y") : <span>Pick a date</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" selected={date} onSelect={onSelect} />
        </PopoverContent>
      </Popover>
    </div>
  );
}

function DownloadSection({ report }: { report: MultiDayAggregateFees }) {
  const [isDownloading, setIsDownloading] = React.useState(false);

  // Generate CSV from report data
  const generateLocalCSV = () => {
    // Sort days in ascending order
    const sortedDays = [...report.days].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    // Get all unique fee tiers
    const feeTiers = Array.from(
      new Set(report.fee_tier_breakdown.map((tier) => tier.item))
    );

    // Create CSV header
    const headers = [
      "Date",
      ...feeTiers.map((tier) =>
        tier.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
      ),
      "Total",
    ];

    // Create CSV rows
    const rows = sortedDays.map((day) => {
      const rowData = [day.date];

      // Add fee amounts for each tier
      feeTiers.forEach((tierName) => {
        const tierFee = day.fee_tier_breakdown.find(
          (tier) => tier.item === tierName
        );
        rowData.push(((tierFee?.amount_cents ?? 0) / 100).toString());
      });

      // Add total for the day
      rowData.push((day.total_fees / 100).toString());

      return rowData;
    });

    // Create CSV content
    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    // Create and download the file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `daily_breakdown_${report.start_date}_${report.end_date}.csv`
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  // Handle download click for full flight data
  const handleDownloadFlights = async () => {
    try {
      setIsDownloading(true);

      const response = await fetch(
        `${API_ENDPOINT}/api/landing/report/range?start_date=${report.start_date}&end_date=${report.end_date}&export=flights`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
          },
          body: JSON.stringify({
            filter: { include_diversion: false },
          }),
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to download flights");
      }

      const csvData = await response.text();

      // Create and download the file
      const blob = new Blob([csvData], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `flights_${report.start_date}_${report.end_date}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download flights:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="mt-6 border-t border-gray-600 pt-6">
      <div className="flex items-center justify-end gap-4">
        <button
          onClick={generateLocalCSV}
          className="text-blue-400 hover:text-blue-300 hover:bg-slate-600 text-sm bg-slate-700 px-4 py-2 rounded-lg"
        >
          Download Daily Breakdown CSV
        </button>
        <button
          onClick={handleDownloadFlights}
          disabled={isDownloading}
          className="text-blue-400 hover:text-blue-300 hover:bg-slate-600 text-sm bg-slate-700 px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isDownloading ? (
            <>
              <span className="animate-spin">⏳</span>
              Downloading...
            </>
          ) : (
            "Download All Flights CSV"
          )}
        </button>
      </div>
    </div>
  );
}

function DateRangeReportSummary({ report }: { report: MultiDayAggregateFees }) {
  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(cents / 100);
  };

  const formatDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(
      "en-US",
      {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      }
    );
  };

  // Calculate total flights
  const totalFlights = report.days.reduce(
    (sum, day) => sum + day.flight_count,
    0
  );

  return (
    <div className="border-gray-600 border bg-slate-900 rounded-lg shadow-lg p-6 w-full text-slate-200">
      <h2 className="text-xl font-semibold mb-4 text-slate-100">
        Landing Fee Report
      </h2>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-8 gap-4 mb-6">
        <div className="bg-slate-700 p-4 rounded-lg lg:col-span-2">
          <div className="text-sm text-slate-400">Total Revenue</div>
          <div className="text-2xl font-bold text-slate-100">
            {formatCurrency(report.total_fees)}
          </div>
        </div>
        <div className="bg-slate-700 p-4 rounded-lg lg:col-span-2">
          <div className="text-sm text-slate-400">Total Flights</div>
          <div className="text-2xl font-bold text-slate-100">
            {totalFlights}
          </div>
        </div>
        <div className="bg-slate-700 p-4 rounded-lg sm:col-span-2 lg:col-span-4">
          <div className="text-sm text-slate-400">Date Range</div>
          <div className="text-lg font-bold text-slate-100">
            {formatDate(report.start_date)} - {formatDate(report.end_date)}
          </div>
          <div className="text-sm text-slate-400 mt-1">
            ({report.num_days} days)
          </div>
        </div>
      </div>

      {/* Breakdowns */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
        {/* Flight Type Breakdown */}
        <div>
          <h3 className="text-sm font-semibold text-slate-400 mb-2">
            Flight Types
          </h3>
          <div className="space-y-2">
            {report.flight_type_breakdown.map((item) => (
              <div key={item.item} className="flex justify-between">
                <span className="text-sm text-slate-300">{item.item}</span>
                <span className="font-medium text-slate-200">
                  {formatCurrency(item.amount_cents)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Carrier Breakdown - Show only carriers with non-zero amounts */}
        <div>
          <h3 className="text-sm font-semibold text-slate-400 mb-2">
            Top Carriers
          </h3>
          <div className="space-y-2">
            {report.carrier_breakdown
              .filter((carrier) => carrier.amount_cents > 0)
              .sort((a, b) => b.amount_cents - a.amount_cents)
              .slice(0, 5)
              .map((item) => (
                <div key={item.item} className="flex justify-between">
                  <span className="text-sm text-slate-300">{item.item}</span>
                  <span className="font-medium text-slate-200">
                    {formatCurrency(item.amount_cents)}
                  </span>
                </div>
              ))}
          </div>
        </div>

        {/* Fee Tier Breakdown */}
        <div>
          <h3 className="text-sm font-semibold text-slate-400 mb-2">
            Fee Tiers
          </h3>
          <div className="space-y-2">
            {report.fee_tier_breakdown.map((item) => (
              <div key={item.item} className="flex justify-between">
                <span className="text-sm text-slate-300">
                  {item.item
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                </span>
                <span className="font-medium text-slate-200">
                  {formatCurrency(item.amount_cents)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Download Section */}
      <DownloadSection report={report} />
    </div>
  );
}

function DailyFeeChart({ report }: { report: MultiDayAggregateFees }) {
  // Transform the data for the chart
  const feeTiers = Array.from(
    new Set(report.fee_tier_breakdown.map((tier) => tier.item))
  );

  // Sort days in ascending order
  const sortedDays = [...report.days].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  // Create series data for each fee tier
  const series = feeTiers.map((tierName) => {
    return {
      name: tierName
        .replace(/_/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase()),
      values: sortedDays.map((day) => {
        // Find the fee amount for this tier on this day
        const tierFee = day.fee_tier_breakdown.find(
          (tier) => tier.item === tierName
        );
        return tierFee ? tierFee.amount_cents / 100 : 0; // Convert cents to dollars
      }),
    };
  });

  // Create labels from dates
  const labels = sortedDays.map((day) =>
    new Date(day.date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    })
  );

  return (
    <div className="border-gray-600 border bg-slate-900 rounded-lg shadow-lg p-6 mt-6 w-full">
      <h2 className="text-xl font-semibold mb-8 text-slate-100">
        Daily Fee Breakdown
      </h2>
      <div className="h-[250px] md:h-[400px]">
        <MultiSeriesChart
          series={series}
          labels={labels}
          xAxisTitle="Date"
          yAxisTitle="Revenue ($)"
          chartType="column"
          stacked={true}
        />
      </div>
    </div>
  );
}

function DateRangeDailyBreakdown({
  report,
}: {
  report: MultiDayAggregateFees;
}) {
  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(cents / 100);
  };

  const formatDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(
      "en-US",
      {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      }
    );
  };

  // Sort days in ascending order
  const sortedDays = [...report.days].sort((a, b) => {
    const [aYear, aMonth, aDay] = a.date.split("-").map(Number);
    const [bYear, bMonth, bDay] = b.date.split("-").map(Number);
    return (
      Date.UTC(aYear, aMonth - 1, aDay) - Date.UTC(bYear, bMonth - 1, bDay)
    );
  });

  return (
    <div className="border-gray-600 border bg-slate-900 rounded-lg shadow-lg p-6 mt-6 w-full text-slate-200">
      <h2 className="text-xl font-semibold mb-4 text-slate-100">
        Daily Breakdown
      </h2>

      <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="text-sm text-slate-400 border-b border-slate-700">
            <th className="text-left pb-2">Date</th>
            <th className="text-right pb-2">Flights</th>
            <th className="text-right pb-2">Revenue</th>
          </tr>
        </thead>
        <tbody>
          {sortedDays.map((day) => (
            <tr
              key={day.date}
              className="border-b border-slate-700/50 hover:bg-slate-700/30"
            >
              <td className="py-3">
                <a
                  href={`/data/landing/day/${day.date}`}
                  className="text-blue-400 hover:text-blue-300 hover:underline"
                >
                  {formatDate(day.date)}
                </a>
              </td>
              <td className="text-right pt-3">
                {formatCurrency(report.total_fees)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="text-sm font-semibold">
            <td className="pt-3">Total</td>
            <td className="text-right pt-3">
              {sortedDays.reduce((sum, day) => sum + day.flight_count, 0)}
            </td>
            <td className="text-right pt-3">
              {formatCurrency(report.total_fees)}
            </td>
          </tr>
        </tfoot>
      </table>
      </div>
    </div>
  );
}

function DateRangePicker() {
  const { data: availableDates } = getAvailableDates();
  const { setStartDate, setEndDate } = useDateContext();
  const [visualStartDate, setVisualStartDate] = React.useState<string | null>(
    null
  );
  const [visualEndDate, setVisualEndDate] = React.useState<string | null>(null);

  function createUTCDate(dateString: string | null) {
    if (!dateString) return undefined;
    const [year, month, day] = dateString.split("-").map(Number);
    return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  }

  function formatDateForContext(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  function formatDateForVisual(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  // Update context when both dates are selected
  React.useEffect(() => {
    if (visualStartDate && visualEndDate) {
      const startDate = createUTCDate(visualStartDate);
      const endDate = createUTCDate(visualEndDate);
      if (startDate && endDate) {
        setStartDate(formatDateForContext(startDate));
        setEndDate(formatDateForContext(endDate));
      }
    }
  }, [visualStartDate, visualEndDate, setStartDate, setEndDate]);

  function isDateDisabled(date: Date, isStartCalendar: boolean) {
    // Check if date is available
    const dateStr = formatDateForVisual(date);
    const isAvailable = availableDates?.includes(dateStr);

    if (!isAvailable) return true;

    // For start calendar, disable dates after end date
    if (isStartCalendar && visualEndDate) {
      return date > createUTCDate(visualEndDate)!;
    }
    // For end calendar, disable dates before start date
    if (!isStartCalendar && visualStartDate) {
      return date < createUTCDate(visualStartDate)!;
    }
    return false;
  }

  const handleDateSelect = (date: Date | undefined, isStart: boolean) => {
    if (date) {
      const dateStr = formatDateForVisual(date);
      if (isStart) {
        setVisualStartDate(dateStr);
      } else {
        setVisualEndDate(dateStr);
      }
    }
  };

  return (
    <div className="border-b border-gray-600 px-4 py-3">
      <div className="text-sm font-medium text-slate-400 mb-2 sm:hidden">
        Select Dates:
      </div>
      <div className="flex flex-wrap items-center gap-2 sm:gap-4">
        <div className="text-sm font-medium text-slate-400 whitespace-nowrap hidden sm:block">
          Select Dates:
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant={"outline"}
              className={cn(
                "w-[160px] sm:w-[200px] justify-start text-left font-normal",
                !visualStartDate && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
              {visualStartDate ? (
                format(createUTCDate(visualStartDate)!, "LLL dd, y")
              ) : (
                <span>Start date</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={createUTCDate(visualStartDate)}
              onSelect={(date) => handleDateSelect(date, true)}
              disabled={(date) => isDateDisabled(date, true)}
              initialFocus
            />
          </PopoverContent>
        </Popover>

        <div className="text-sm text-slate-500">→</div>

        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant={"outline"}
              className={cn(
                "w-[160px] sm:w-[200px] justify-start text-left font-normal",
                !visualEndDate && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
              {visualEndDate ? (
                format(createUTCDate(visualEndDate)!, "LLL dd, y")
              ) : (
                <span>End date</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={createUTCDate(visualEndDate)}
              onSelect={(date) => handleDateSelect(date, false)}
              disabled={(date) => isDateDisabled(date, false)}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

function Sidebar() {
  return (
    <div className="space-y-6">
      <FilterBuilder />
    </div>
  );
}

export function DateRangePage() {
  const { setTitles } = usePage();
  const { appliedFilters } = useFilterContext();
  const { startDate, endDate, setStartDate, setEndDate } = useDateContext();

  useEffect(() => {
    setTitles(["Landing Fees"]);
  }, [setTitles]);

  const { data: rangeReport } = getRangeReport({
    startDate: startDate ?? "",
    endDate: endDate ?? "",
    filter: appliedFilters,
    enabled: Boolean(startDate && endDate),
  });

  return (
    <div>
      <DateRangePicker />
      {!startDate || !endDate ? (
        <div className="flex items-center justify-center h-[calc(100vh-120px)]">
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-slate-300 mb-2">
              Please Select Dates
            </h2>
            <p className="text-slate-400">
              Select a date range above to view the landing fees report.
            </p>
          </div>
        </div>
      ) : (
        <div className="flex w-full p-4">
          <div className="w-[300px] flex-shrink-0">
            <Sidebar />
          </div>
          <div className="ml-4 flex-1">
            {rangeReport && (
              <>
                <DateRangeReportSummary report={rangeReport} />
                <DailyFeeChart report={rangeReport} />
                <DateRangeDailyBreakdown report={rangeReport} />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
